import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BufferTracker } from '@bufferapp/buffer-tracking-browser-ts';
import { useAccount } from '~/accountContext';
import PropTypes from 'prop-types';
import getURL from '~/server/formatters/getURL';

import { ProgressBar, Text } from '@bufferapp/ui';
import CheckmarkIcon from '@bufferapp/ui/Icon/Icons/Checkmark';
import CrossIcon from '@bufferapp/ui/Icon/Icons/Cross';
import CaretRightIcon from '@bufferapp/ui/Icon/Icons/CaretRight';
import { TrophyIllustration } from '~/welcome-checklist/icons/trophy-illustration';
import { useSplitEnabledWithConfig } from '@bufferapp/features';
import { actions as welcomeChecklistActions } from '../../reducer';
import { useChecklists } from '../hooks/useChecklists';
import { selectChecklist } from '../utils/selectChecklist';
import { getChecklistProgress } from '../utils/getChecklistProgress';

import {
  Checklist,
  ChecklistFooter,
  ChecklistHeader,
  ChecklistItem,
  ChecklistTitle,
  CheckOn,
  CheckOff,
  ProgressBarWrapper,
  WelcomeChecklistWrapper,
  ChecklistItemLeft,
  BottomStyleWrapper,
  IconWrapper,
} from './style';

const COMPLETED_PROGRESS = 100;
const CHECKLIST_MODAL_DIMISSED_LOCAL_STORAGE_KEY =
  'isChecklistCompleteModalDismissed';

const WelcomeChecklist = ({
  onPublishFirstPostClick,
  onCreateIdeaClick,
  setShowAddChannels,
  setShowManageChannels,
}) => {
  const { account } = useAccount();
  const { checklists, loading, error, refetch } = useChecklists();
  const dispatch = useDispatch();

  // Check if we should refetch the checklist status based on app state
  const shouldRefetch = useSelector(
    (state) => state.welcomeChecklist.shouldRefetch
  );
  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      dispatch(welcomeChecklistActions.clearShouldRefetch());
    }
  }, [dispatch, refetch, shouldRefetch, account]);

  // Experiment 75 is in progress and implies to show Create as a new menu item at top menu, so
  // if EID75 is actived we should go to ideas using window.location.assign
  const currentOrganizationCreationDate =
    account?.currentOrganization?.createdAt;

  const epochsMs = currentOrganizationCreationDate
    ? new Date(currentOrganizationCreationDate).getTime()
    : 0;

  const { isEnabled: isEID75SegmentBEnabled } = useSplitEnabledWithConfig(
    'EID75-create-mainmenu-vs-sidebar',
    { signUpDate: epochsMs },
    'b'
  );

  // TODO: currently hardcoded for showing checklist 1 only. We'll make this more dynamic as we add checklists
  const noOp = useCallback(() => undefined, []);
  const WELCOME_CHECKLIST_LINKS = {
    1: () => noOp(),
    2: () => window.location.assign(getURL.getChannelConnectURL()),
    3: () => onCreateIdeaClick(isEID75SegmentBEnabled),
    4: () =>
      onPublishFirstPostClick({
        selectedProfileId: null,
        selectedProfileIds: [],
      }),
  };

  // TODO: Don't show the dismiss button for the initial checklist
  // Future checklists will have a dismiss button
  const shouldShowDismissButton = false;

  let checklist = {};
  let checklistProgress = {
    itemsCompleted: 0,
    itemsTotal: 0,
    progressPercent: 0,
  };
  if (checklists) {
    checklist = selectChecklist(checklists);
    checklistProgress = getChecklistProgress(checklist);
  }

  const shouldShowProgressBar = checklistProgress.progressPercent > 0;

  const handleChecklistItemClick = (id, checklistItem, isCompleted) => {
    // Don't track clicks on the first item
    if (id === 1) {
      return;
    }
    const eventData = {
      organizationId: account?.currentOrganization?.id || '',
      checklistId: checklist?.id || 0,
      checklistItem: checklistItem || '',
      isCompleted: isCompleted || false,
      qtyItemsCompleted: checklistProgress.itemsCompleted,
    };

    BufferTracker.checklistItemClicked(eventData);

    WELCOME_CHECKLIST_LINKS[id]();
  };

  const dismissedChecklist =
    account?.currentOrganization?.dismissedObjects?.find(
      (item) => item.id === 'welcome_checklist_complete'
    ) || false;

  const isChecklistCompleteModalDismissed =
    dismissedChecklist ||
    !!localStorage.getItem(CHECKLIST_MODAL_DIMISSED_LOCAL_STORAGE_KEY);

  const isChecklistComplete =
    checklistProgress.progressPercent === COMPLETED_PROGRESS;
  useEffect(() => {
    const { MODALS, actions } = window?.appshell || {};
    if (actions && isChecklistComplete && !isChecklistCompleteModalDismissed) {
      actions.openModal(MODALS.checklistComplete);
    }
  }, [isChecklistComplete, isChecklistCompleteModalDismissed]);

  // Hide the checklist if we've seen the modal and the checklist is complete
  const shouldHideChecklist =
    isChecklistCompleteModalDismissed && isChecklistComplete;

  useEffect(() => {
    if (!shouldHideChecklist) {
      BufferTracker.welcomeChecklistViewed({
        organizationId: account?.currentOrganization?.id,
        qtyItemsCompleted: checklistProgress.itemsCompleted,
      });
    }
  });

  // If we hide the checklist, show the add channels and manage channels buttons
  if (shouldHideChecklist) {
    setShowAddChannels(true);
    setShowManageChannels(true);
  } else {
    setShowManageChannels(false);
    const channelCount = account?.currentOrganization?.channels?.length || 0;
    if (channelCount < 1 || channelCount > 3) {
      setShowAddChannels(false);
    }
  }

  if (shouldHideChecklist || !checklist || !checklists || loading || error) {
    return null;
  }

  return (
    <WelcomeChecklistWrapper data-testid="welcome_checklist">
      <BottomStyleWrapper>
        <ChecklistHeader>
          <IconWrapper>
            <TrophyIllustration />
          </IconWrapper>
          <ChecklistTitle>
            <Text type="p">{checklist.title}</Text>
            {shouldShowDismissButton && <CrossIcon />}
          </ChecklistTitle>
          {shouldShowProgressBar && (
            <ProgressBarWrapper>
              <Text type="p">
                {checklistProgress.itemsCompleted} of{' '}
                {checklistProgress.itemsTotal}
              </Text>
              <ProgressBar
                className="checklistProgress"
                progress={`${checklistProgress.progressPercent}%`}
              />
            </ProgressBarWrapper>
          )}
        </ChecklistHeader>
        <Checklist>
          {checklist.items.map((item) => {
            return (
              <ChecklistItem
                key={item.description}
                onClick={() => {
                  handleChecklistItemClick(
                    item.id,
                    item.description,
                    item.isCompleted
                  );
                }}
                initial={item.id === 1}
              >
                <ChecklistItemLeft isCompleted={item.isCompleted}>
                  {item.isCompleted ? (
                    <CheckOn>
                      <CheckmarkIcon size="medium" />
                    </CheckOn>
                  ) : (
                    <CheckOff />
                  )}
                  <Text type="p">{item.description}</Text>
                </ChecklistItemLeft>
                {item.id !== 1 && <CaretRightIcon size="medium" />}
              </ChecklistItem>
            );
          })}
        </Checklist>
        <ChecklistFooter>
          <Text type="p">
            Need some help?{' '}
            <a
              id="checklist-help-link"
              href="https://support.buffer.com/hc/en-us/articles/8385502935315-Getting-started-with-Buffer-s-publishing-features"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read our guide
            </a>
          </Text>
        </ChecklistFooter>
      </BottomStyleWrapper>
    </WelcomeChecklistWrapper>
  );
};

WelcomeChecklist.propTypes = {
  onPublishFirstPostClick: PropTypes.func.isRequired,
  onCreateIdeaClick: PropTypes.func.isRequired,
  setShowAddChannels: PropTypes.func,
  setShowManageChannels: PropTypes.func,
};

WelcomeChecklist.defaultProps = {
  setShowAddChannels: () => {},
  setShowManageChannels: () => {},
};

export default WelcomeChecklist;
